import { DateOnly } from '@if/core/date'
import {
  EContractStatus,
  ECreditApplicationStatus,
  ECreditApplicationStep,
  EDecision,
  EDocumentStatus,
  EDocumentType,
  EOriginSystemId,
  EWorksheetStatus,
} from '@if/core/types'
import { Action, ActionList, Banner } from '@if/mui'
import { FileSignature } from 'lucide-react'
import {
  areAllRequiredDocumentForStepApproved,
  availableCreditEqualApprovedAmount,
  isContractExpired,
} from '@src/data/creditapp-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ECreditApplicationStatusMessage, GetContractStatus } from '../../viewCreditApp-selectors'
import { canSendOrDeleteHighEstimateInvoice } from '../DocumentSection/documentTableRow-hooks'
import ContractAwaitingSignature from '../ContractSection/ContractAwaitingSignature'
import ContractCompleted from '../ContractSection/ContractCompleted'
import StageContent from './StageContent'

interface Props {
  creditApp: CreditApplication
  isPolling: boolean
  canEdit: boolean
}

// Contract descriptor for StageContent: the contract flow (gating messages + reachable states).
const ContractStageContent = ({ creditApp, isPolling, canEdit }: Props) => {
  const { t } = useTranslation()
  const config = FinancingProgramConfigs[creditApp.financingProgramId]

  // The contract-expiry feature is gated by the program config.
  const isExpiryFeatureActive = config?.supportsContractUpdateOnExpiry === true
  const expired = isContractExpired(creditApp, isExpiryFeatureActive)
  const status = expired
    ? ECreditApplicationStatusMessage.Expired
    : GetContractStatus(creditApp, creditApp.worksheet).status

  // A warranty contract, when required by the program, must be provided before creating the CVT.
  const canCreateContract =
    !config?.mustProvideWarrantyContract ||
    !!creditApp.requiredDocuments.find(
      (doc) => doc.typeId === EDocumentType.ExtendedWarrantyContract && doc.status !== EDocumentStatus.AwaitingDocument,
    )
  const allDocumentsRequiredForCvtApproved =
    areAllRequiredDocumentForStepApproved(creditApp, ECreditApplicationStep.CVT) &&
    areAllRequiredDocumentForStepApproved(creditApp, ECreditApplicationStep.Financing)
  const isWorksheetApproved = creditApp.worksheet?.finalDecision.decision === EDecision.Approved
  const isAvailableCreditEqualApprovedAmount = availableCreditEqualApprovedAmount(creditApp)

  // The loan/contract is still being generated on the server → StageContent shows a skeleton.
  const isContractCreating =
    isWorksheetApproved && !expired && creditApp.contract.status === EContractStatus.NotReady && isPolling

  // A high-estimate clinic invoice can no longer be sent once its delay has elapsed.
  const highEstimateInvoiceDelayExpired = useMemo(
    () =>
      Boolean(
        creditApp.worksheet?.isHighEstimate &&
          creditApp.worksheet.firstPaymentOn &&
          canEdit &&
          creditApp.requiredDocuments.find((x) => x.typeId === EDocumentType.ClinicInvoice) &&
          !canSendOrDeleteHighEstimateInvoice(new DateOnly(creditApp.worksheet.firstPaymentOn)),
      ),
    [canEdit, creditApp],
  )

  const showCreateComponent =
    (creditApp.worksheet == null ||
      creditApp.worksheet.status === EWorksheetStatus.Draft ||
      creditApp.worksheet.finalDecision.decision === EDecision.ManualRevision ||
      highEstimateInvoiceDelayExpired) &&
    canEdit &&
    canCreateContract

  // Navigates to the worksheet to create/edit the contract (label depends on the worksheet state).
  const createContractAction: Action = {
    icon: FileSignature,
    label:
      creditApp.worksheet != null && creditApp.worksheet.status !== EWorksheetStatus.Draft
        ? t('worksheet.editWorksheet')
        : t('worksheet.createContract'),
    href: `/credits/${creditApp.financingProgramId}/${creditApp.id}/worksheet`,
  }

  // Gating messages first, then the reachable contract states.
  const renderContent = () => {
    if (creditApp.finalCreditDecision.decision !== EDecision.Approved) {
      return <Banner severity="info">{t('viewCreditApplication.creditNotApproved')}</Banner>
    }
    if (
      config.worksheetIsNeededForCredit &&
      creditApp.worksheet === null &&
      creditApp.consentHardHit === true &&
      creditApp.originSystemId === EOriginSystemId.Subscription
    ) {
      return <Banner severity="info">{t('viewCreditApplication.creditNotApproved')}</Banner>
    }
    if (!creditApp.allIncomesConfirmed) {
      return <Banner severity="info">{t('viewCreditApplication.incomeNotCompleted')}</Banner>
    }
    if (creditApp.hasTasksBeforeCvt) {
      return <Banner severity="warning">{t('viewCreditApplication.cvtTasksNotCompleted')}</Banner>
    }
    if (!allDocumentsRequiredForCvtApproved) {
      return (
        <>
          {status === ECreditApplicationStatusMessage.DocumentsRequired && (
            <Banner severity="warning">{t('viewCreditApplication.requiredDocuments')}</Banner>
          )}
          {status === ECreditApplicationStatusMessage.AwaitingApproval && (
            <Banner severity="info">{t('viewCreditApplication.documentsAwaitingApproval')}</Banner>
          )}
        </>
      )
    }
    return (
      <>
        {status === ECreditApplicationStatusMessage.Cancelled && (
          <Banner severity="error">{t('viewCreditApplication.creditAppCancelled')}</Banner>
        )}
        {!isAvailableCreditEqualApprovedAmount && showCreateComponent && (
          <Banner severity="warning">{t('viewCreditApplication.availableCreditNotCorresponding')}</Banner>
        )}
        {!canCreateContract && (
          <Banner severity="warning">{t('viewCreditApplication.extendedWarrantyContractRequired')}</Banner>
        )}
        {!showCreateComponent &&
          (creditApp.worksheet == null || !isWorksheetApproved) &&
          creditApp.status !== ECreditApplicationStatus.Cancelled && (
            <Banner severity="info">{t('worksheet.contractNotCreated')}</Banner>
          )}
        {isAvailableCreditEqualApprovedAmount &&
          showCreateComponent &&
          creditApp.status !== ECreditApplicationStatus.Cancelled && (
            <ActionList
              actions={[createContractAction]}
              variant="button"
              menuLabel={t('viewCreditApplication.actions')}
            />
          )}
        {isWorksheetApproved && creditApp.contract.status === EContractStatus.Completed && (
          <ContractCompleted creditApp={creditApp} />
        )}
        {isWorksheetApproved &&
          (expired ||
            creditApp.contract.status === EContractStatus.Ready ||
            creditApp.contract.status === EContractStatus.Sending ||
            creditApp.contract.status === EContractStatus.UnderReview) && (
            <ContractAwaitingSignature
              creditApp={creditApp}
              isExpiryFeatureActive={isExpiryFeatureActive}
              isExpired={expired}
            />
          )}
        {isWorksheetApproved && !expired && creditApp.contract.status === EContractStatus.NotReady && !isPolling && (
          <Banner severity="error">{t('worksheet.error')}</Banner>
        )}
      </>
    )
  }

  return <StageContent customContent={renderContent()} loading={isContractCreating} />
}

export default ContractStageContent
